/**
 * English: Hooks for /api/usage in-memory stats and PostgreSQL-backed usage history.
 * Thai: hooks สำหรับสถิติ /api/usage ใน memory และประวัติจาก PostgreSQL
 *
 * Uses useApi() so Authorization: Bearer is sent — backend requires JWT for /api/usage*.
 */
import { useState, useEffect, useCallback } from 'react';
import { ENDPOINTS } from '../config/api';
import { useApi } from './useApi';

export type ModelStats = {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
};

export type UsageResponse = {
  session_started_at: string;
  total_calls: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  by_model: Record<string, ModelStats>;
};

export type DailyUsage = {
  date: string;
  calls: number;
  tokens: number;
  cost_usd: number;
};

export type OrgUsage = {
  org_name: string;
  calls: number;
  tokens: number;
  cost_usd: number;
};

export type UsageHistoryResponse = {
  days: number;
  filter_org_name?: string | null;
  total_calls: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  by_model: Record<string, ModelStats>;
  by_org?: OrgUsage[];
  daily: DailyUsage[];
  error?: string;
};

export type UsageLogItem = {
  id: number;
  task_id: string;
  node: string;
  model_name: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  org_name: string | null;
  user_sub: string | null;
  client_source: string | null;
  created_at: string | null;
};

export type UsageLogResponse = {
  days: number;
  filter_org_name?: string | null;
  limit: number;
  offset: number;
  total: number;
  items: UsageLogItem[];
  error?: string;
};

export function useUsage() {
  const { get } = useApi();
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const json = (await get(ENDPOINTS.usage, {})) as UsageResponse;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch usage');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

export function useUsageHistory(days: number = 7, orgFilter: string = '') {
  const { get } = useApi();
  const [data, setData] = useState<UsageHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const trimmed = orgFilter.trim();
      const params: Record<string, string> = { days: String(days) };
      if (trimmed) params.org_name = trimmed;
      const json = (await get(ENDPOINTS.usageHistory, params)) as UsageHistoryResponse;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch usage history');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [get, days, orgFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

const DEFAULT_PAGE_SIZE = 50;

export function useUsageLog(
  days: number,
  orgFilter: string,
  page: number,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  const { get } = useApi();
  const [data, setData] = useState<UsageLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const trimmed = orgFilter.trim();
      const offset = Math.max(0, page) * pageSize;
      const params: Record<string, string> = {
        days: String(days),
        limit: String(pageSize),
        offset: String(offset),
      };
      if (trimmed) params.org_name = trimmed;
      const json = (await get(ENDPOINTS.usageLog, params)) as UsageLogResponse;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch usage log');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [get, days, orgFilter, page, pageSize]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh, pageSize };
}
